// Main application router with lazy-loaded routes for performance
import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { RootLayout } from '@/components/layout/root-layout'
import { AuthenticatedLayout } from '@/components/layout/authenticated-layout'
import { ProtectedRoute } from '@/components/auth/protected-route'
import { Skeleton } from '@/components/ui/skeleton'

// Eager-loaded pages (critical path for auth and onboarding)
import { LoginPage } from '@/pages/login'
import { SignupPage } from '@/pages/signup'
import { ConfirmEmailPage } from '@/pages/confirm-email'
import { OnboardingPage } from '@/pages/onboarding'
import { LocationSearchPage } from '@/pages/location-search'
import { AuthCallbackPage } from '@/pages/auth-callback'

// Eager-loaded primary pages (avoid Suspense flash on common navigation paths)
import { ReviewsPage } from '@/pages/reviews'

// Lazy-loaded pages (code-split for smaller initial bundle)
const DashboardPage = lazy(() =>
  import('@/pages/dashboard').then((m) => ({ default: m.DashboardPage }))
)
const CompetitorsPage = lazy(() =>
  import('@/pages/competitors').then((m) => ({ default: m.CompetitorsPage }))
)
const AddCompetitorPage = lazy(() =>
  import('@/pages/add-competitor').then((m) => ({ default: m.AddCompetitorPage }))
)
const InsightsPage = lazy(() =>
  import('@/pages/insights').then((m) => ({ default: m.InsightsPage }))
)
const TrendsPage = lazy(() => import('@/pages/trends').then((m) => ({ default: m.TrendsPage })))
// Staff-only; the nav entry is gated on the admin_tools flag. The route stays
// reachable by URL for an admin who bookmarks it — the API is the real gate.
const AdminSnapshotsPage = lazy(() =>
  import('@/pages/admin-snapshots').then((m) => ({ default: m.AdminSnapshotsPage }))
)
const SettingsPage = lazy(() =>
  import('@/pages/settings').then((m) => ({ default: m.SettingsPage }))
)

// Loading fallback for lazy-loaded pages
function PageSkeleton() {
  return (
    <div className="p-6 space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-32 rounded-lg" />
      </div>
      <Skeleton className="h-64 rounded-lg" />
    </div>
  )
}

function App() {
  return (
    <BrowserRouter>
      <RootLayout>
        <Routes>
          {/* Public routes (no sidebar) */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />

          {/* Location search requires auth but no sidebar */}
          <Route
            path="/locations/search"
            element={
              <ProtectedRoute>
                <LocationSearchPage />
              </ProtectedRoute>
            }
          />

          {/* Authenticated routes (with sidebar, lazy-loaded) */}
          <Route
            element={
              <ProtectedRoute>
                <AuthenticatedLayout />
              </ProtectedRoute>
            }
          >
            <Route
              path="/dashboard"
              element={
                <Suspense fallback={<PageSkeleton />}>
                  <DashboardPage />
                </Suspense>
              }
            />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route
              path="/competitors"
              element={
                <Suspense fallback={<PageSkeleton />}>
                  <CompetitorsPage />
                </Suspense>
              }
            />
            <Route
              path="/competitors/add"
              element={
                <Suspense fallback={<PageSkeleton />}>
                  <AddCompetitorPage />
                </Suspense>
              }
            />
            <Route
              path="/insights"
              element={
                <Suspense fallback={<PageSkeleton />}>
                  <InsightsPage />
                </Suspense>
              }
            />
            <Route
              path="/trends"
              element={
                <Suspense fallback={<PageSkeleton />}>
                  <TrendsPage />
                </Suspense>
              }
            />
            <Route
              path="/settings"
              element={
                <Suspense fallback={<PageSkeleton />}>
                  <SettingsPage />
                </Suspense>
              }
            />
            <Route
              path="/admin/snapshots"
              element={
                <Suspense fallback={<PageSkeleton />}>
                  <AdminSnapshotsPage />
                </Suspense>
              }
            />
          </Route>
        </Routes>
      </RootLayout>
    </BrowserRouter>
  )
}

export default App
